import React from 'react';

import { useTranslation } from 'react-i18next';
import { Icon } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import type { TemplateSection } from 'inspection-types';
import { S } from 'components/typography';

import {
  computeCustomizeItemOrdinalIndex,
  formatSectionTitle,
} from '../../helpers/inspectionTemplateCustomizeHelpers';

import { Item } from './SectionCardItem';
import styles from './InspectionTemplateCustomizeSectionCard.scss';

const NO_IDS_LOADING = new Set<string>();

type Props = {
  section: TemplateSection;
  sectionIndex: number;
  expanded: boolean;
  onToggle: () => void;
  removeSectionDisabledTitle: string;
  removeItemDisabledTitle: string;
  onRemoveItem?: (itemId: string) => void;
  removingItemIds?: ReadonlySet<string>;
  onRestoreItem?: (itemId: string) => void;
  restoringItemIds?: ReadonlySet<string>;
  /** Item IDs shown as removed (strikethrough); defaults to empty. */
  removedOverlayItemIds?: ReadonlySet<string>;
};

export function SectionCard({
  section,
  sectionIndex,
  expanded,
  onToggle,
  removeSectionDisabledTitle,
  removeItemDisabledTitle,
  onRemoveItem,
  removingItemIds,
  onRestoreItem,
  restoringItemIds,
  removedOverlayItemIds = new Set(),
}: Props) {
  const { t } = useTranslation();
  const removingItemIdSet = removingItemIds ?? NO_IDS_LOADING;
  const restoringItemIdSet = restoringItemIds ?? NO_IDS_LOADING;

  const itemCount = section.items.length;
  const items = [...section.items].sort((a, b) => a.order - b.order);

  return (
    <div id={`section-${section.id}`} className={styles.sectionCard}>
      <div className={styles.sectionHeaderRow}>
        <button
          type="button"
          className={styles.sectionToggle}
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <span className={styles.rowLead} aria-hidden>
            <Icon
              name={expanded ? 'chevron-down' : 'chevron-right'}
              set={ICON_SET_FONTAWESOME}
              type="fas"
              className={styles.sectionChevron}
            />
          </span>
          <span className={styles.sectionBody}>
            <span className={styles.sectionTitle}>
              {formatSectionTitle(sectionIndex, section.name)}
            </span>
            <S variant="secondary" className={styles.sectionItemsIncluded}>
              {t('web.admin.inspections.templateCustomize.itemsIncluded', {
                count: itemCount,
                included: itemCount,
                total: itemCount,
              })}
            </S>
          </span>
        </button>
        <div className={styles.rowActionCell}>
          <button
            type="button"
            className={styles.rowActionButton}
            disabled
            title={removeSectionDisabledTitle}
            aria-label={t(
              'web.admin.inspections.templateCustomize.removeSectionAria'
            )}
          >
            <Icon name="trash-alt" set={ICON_SET_FONTAWESOME} type="far" />
          </button>
        </div>
      </div>
      {expanded &&
        items.map((item, itemIndex) => (
          <Item
            key={item.id}
            item={item}
            sectionIndex={sectionIndex}
            itemOrdinalSegmentIndex={computeCustomizeItemOrdinalIndex(
              items,
              itemIndex,
              removedOverlayItemIds
            )}
            removeItemDisabledTitle={removeItemDisabledTitle}
            isRemovedPresentation={removedOverlayItemIds.has(item.id)}
            onRemoveItem={
              onRemoveItem ? () => onRemoveItem(item.id) : undefined
            }
            isRemoveLoading={removingItemIdSet.has(item.id)}
            onRestoreItem={
              onRestoreItem ? () => onRestoreItem(item.id) : undefined
            }
            isRestoreLoading={restoringItemIdSet.has(item.id)}
          />
        ))}
    </div>
  );
}
